import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18next from 'i18next';
import { getCompanies, createCompany, updateCompany, deleteCompany } from '../api/crmApi';
import type { CompanyFilters, CreateCompanyInput } from '../types';

const STALE_TIME = 2 * 60 * 1000; // 2 minutes

export const crmCompanyKeys = {
  all: ['crm-companies'] as const,
  list: (
    filters?: CompanyFilters,
    page?: number,
    sortBy?: string,
    sortDirection?: 1 | -1,
  ) => [...crmCompanyKeys.all, 'list', filters, page, sortBy, sortDirection] as const,
  competitors: () => [...crmCompanyKeys.all, 'competitors'] as const,
};

export function useCompanies(
  filters?: CompanyFilters,
  page = 0,
  sortBy?: string,
  sortDirection?: 1 | -1,
) {
  return useQuery({
    queryKey: crmCompanyKeys.list(filters, page, sortBy, sortDirection),
    queryFn: () => getCompanies(filters, page, 25, sortBy, sortDirection),
    staleTime: STALE_TIME,
  });
}

export function useCompetitorCompanies() {
  return useQuery({
    queryKey: crmCompanyKeys.competitors(),
    queryFn: () => getCompanies({ type: 'competitor', status: 'active' }, 0, 100, 'name', 1),
    staleTime: 5 * 60 * 1000,
    select: (data) => data.items,
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCompanyInput) => createCompany(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmCompanyKeys.all });
      toast.success(i18next.t('crm:companies.toast.created'));
    },
    onError: (error: Error) => {
      toast.error(i18next.t('crm:companies.toast.failedCreate'), { description: error.message });
    },
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<CreateCompanyInput> & { id: string }) =>
      updateCompany(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmCompanyKeys.all });
      toast.success(i18next.t('crm:companies.toast.updated'));
    },
    onError: (error: Error) => {
      toast.error(i18next.t('crm:companies.toast.failedUpdate'), { description: error.message });
    },
  });
}

export function useDeleteCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCompany(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: crmCompanyKeys.all });
      toast.success(i18next.t('crm:companies.toast.deleted'));
    },
    onError: (error: Error) => {
      toast.error(i18next.t('crm:companies.toast.failedDelete'), { description: error.message });
    },
  });
}
